/**
 * EditOrderDialog Component
 * Allows editing of order details with form validation
 * Handles quantity, delivery date, and notes updates
 *
 * @author SAUBHAGYA Development Team
 * @since US-ORD-006 Phase 5
 */

import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogClose,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import orderService from '@/services/commerce/orderService';
import type { OrderDetails } from '@/types/order';

// ============================================
// TYPES
// ============================================

export interface EditOrderDialogProps {
  orderId: number | null;
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: (updatedOrder: OrderDetails) => void;
}

interface FormData {
  quantity: string;
  requestedDeliveryDate: string;
  notes: string;
}

// ============================================
// COMPONENT
// ============================================

export function EditOrderDialog({
  orderId,
  isOpen,
  onClose,
  onSuccess,
}: EditOrderDialogProps): JSX.Element {
  const [order, setOrder] = useState<OrderDetails | null>(null);
  const [formData, setFormData] = useState<FormData>({
    quantity: '',
    requestedDeliveryDate: '',
    notes: '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen || !orderId) {
      setOrder(null);
      setFormData({ quantity: '', requestedDeliveryDate: '', notes: '' });
      setError(null);
      setFormError(null);
      return;
    }

    const fetchOrder = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const orderData = await orderService.getOrderById(orderId);
        setOrder(orderData);

        // Initialize form with order data
        const firstLineItem = orderData.lineItems?.[0];
        setFormData({
          quantity: firstLineItem?.quantity?.toString() || '',
          requestedDeliveryDate: orderData.delivery?.expectedDeliveryDate || '',
          notes: '',
        });
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Failed to load order details';
        setError(errorMessage);
        console.error('Error fetching order:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchOrder();
  }, [orderId, isOpen]);

  const validateForm = (): boolean => {
    setFormError(null);

    if (!formData.quantity || parseFloat(formData.quantity) <= 0) {
      setFormError('Quantity must be greater than 0');
      return false;
    }

    if (formData.requestedDeliveryDate && !formData.requestedDeliveryDate) {
      setFormError('Please select a valid delivery date');
      return false;
    }

    return true;
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    setFormError(null);
  };

  const handleSave = async () => {
    if (!validateForm() || !orderId) return;

    setIsSaving(true);
    try {
      const updates = {
        quantity: parseFloat(formData.quantity),
        requestedDeliveryDate: formData.requestedDeliveryDate || undefined,
        notes: formData.notes || undefined,
      };

      console.log('[EditOrderDialog] Updating order with:', updates);
      const updatedOrder = await orderService.updateOrder(orderId, updates);

      toast.success('Order Updated', {
        description: `Order ${updatedOrder.orderNumber} has been updated successfully.`,
      });

      if (onSuccess) {
        onSuccess(updatedOrder);
      }

      onClose();
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to update order';
      setError(errorMessage);
      toast.error('Update Failed', {
        description: errorMessage,
      });
      console.error('Error updating order:', err);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Edit Order</DialogTitle>
          <DialogClose />
        </DialogHeader>

        {isLoading && (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
            <span className="ml-2 text-gray-600">Loading order details...</span>
          </div>
        )}

        {error && !isLoading && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {formError && !isLoading && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{formError}</AlertDescription>
          </Alert>
        )}

        {order && !isLoading && (
          <div className="space-y-4">
            {/* Order Info */}
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="text-xs text-gray-500">Order Number</p>
              <p className="text-lg font-semibold text-gray-900">
                {order.orderNumber}
              </p>
            </div>

            {/* Quantity Field */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Quantity
                <span className="text-red-600 ml-1">*</span>
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  name="quantity"
                  value={formData.quantity}
                  onChange={handleInputChange}
                  step="0.01"
                  min="0"
                  placeholder="Enter quantity"
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  disabled={isSaving}
                />
                <span className="text-sm text-gray-500">
                  {order.lineItems?.[0]?.quantityUnit || 'units'}
                </span>
              </div>
            </div>

            {/* Delivery Date Field */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Requested Delivery Date
              </label>
              <input
                type="date"
                name="requestedDeliveryDate"
                value={formData.requestedDeliveryDate}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                disabled={isSaving}
              />
            </div>

            {/* Notes Field */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Notes
              </label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleInputChange}
                placeholder="Add any additional notes..."
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                disabled={isSaving}
              />
            </div>
          </div>
        )}

        <DialogFooter>
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isSaving || isLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={isSaving || isLoading || !order}
            className="bg-blue-600 hover:bg-blue-700"
          >
            {isSaving ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              'Save Changes'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default EditOrderDialog;
